import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useGoBack } from "../../hooks/useGoBack";
import { motion, AnimatePresence } from "framer-motion";
import { apiGet, apiPost } from "../../apiService";
import { connectSocket } from "../../services/socket";
import { getErrorMessage } from "../../utils/errorHelper";
import "./Signup.scss";
import BrandLogo from "../../components/BrandLogo/BrandLogo";

// --- Centralized SVG Icons (Unified to Primary Color #4f46e5) ---
const Icons = {
  ChevronLeft: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2.5"
      width="1.2em"
      height="1.2em"
    >
      <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
  ),
  Shield: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      width="1.2em"
      height="1.2em"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
  ),
  Eye: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      width="1.2em"
      height="1.2em"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
      <circle cx="12" cy="12" r="3"></circle>
    </svg>
  ),
  EyeOff: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      width="1.2em"
      height="1.2em"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
      <line x1="1" y1="1" x2="23" y2="23"></line>
    </svg>
  ),
};

// --- Helper UI Components ---
const AuthCard = ({ children }) => (
  <motion.div
    className="auth-card"
    initial={{ opacity: 0, y: 30, scale: 0.98 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

const CoreNetworkVisual = () => (
  <div className="core-network">
    <svg viewBox="0 0 400 400" fill="none" className="network-svg">
      <defs>
        <filter id="glow-net" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="8" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <circle
        cx="200"
        cy="200"
        r="140"
        stroke="rgba(79, 70, 229, 0.1)"
        strokeWidth="1"
        strokeDasharray="8 12"
        className="anim-spin"
      />
      <path
        d="M100 100 L300 300 M300 100 L100 300"
        stroke="rgba(79, 70, 229, 0.08)"
        strokeWidth="1"
      />
      <g filter="url(#glow-net)">
        <rect
          x="170"
          y="170"
          width="60"
          height="60"
          rx="12"
          fill="#ffffff"
          stroke="#4f46e5"
          strokeWidth="2"
          className="anim-pulse"
        />
        <circle cx="200" cy="200" r="8" fill="#4f46e5" />
      </g>
      <circle
        cx="100"
        cy="100"
        r="6"
        fill="#4f46e5"
        className="anim-float"
        style={{ animationDelay: "0.2s" }}
      />
      <circle
        cx="300"
        cy="300"
        r="6"
        fill="#0ea5e9"
        className="anim-float"
        style={{ animationDelay: "0.8s" }}
      />
    </svg>
    <div className="network-text">
      <h2>Scale Your Infrastructure</h2>
      <p>Secure, compliant APIs for Indian BFSI.</p>
    </div>
  </div>
);

const OTPField = ({ length = OTP_LENGTH, onChange, invalid = false }) => {
  const [vals, setVals] = useState(() => new Array(length).fill(""));
  const inputsRef = useRef([]);

  const setRef = (el, i) => {
    inputsRef.current[i] = el;
  };

  const focusIndex = (i) => {
    const target = inputsRef.current[i];
    if (target) {
      target.focus();
      target.select?.();
    }
  };

  const commit = (next) => {
    setVals(next);
    onChange(next.join(""));
  };

  const handleChange = (i, raw) => {
    // Only digits, take the last entered char (handles autofill / accidental long input)
    const digits = (raw || "").replace(/\D/g, "");
    if (!digits) {
      // Field cleared
      const next = [...vals];
      next[i] = "";
      commit(next);
      return;
    }

    const next = [...vals];
    // If user pasted/typed multiple digits at once, spread across slots from i
    if (digits.length > 1) {
      for (let k = 0; k < digits.length && i + k < length; k += 1) {
        next[i + k] = digits[k];
      }
      commit(next);
      const lastFilled = Math.min(i + digits.length, length) - 1;
      focusIndex(Math.min(lastFilled + 1, length - 1));
      return;
    }

    next[i] = digits;
    commit(next);
    if (i < length - 1) focusIndex(i + 1);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...vals];
      if (next[i]) {
        // Clear current slot, stay put
        next[i] = "";
        commit(next);
      } else if (i > 0) {
        // Empty slot: move back and clear previous
        next[i - 1] = "";
        commit(next);
        focusIndex(i - 1);
      }
      return;
    }
    if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focusIndex(i - 1);
      return;
    }
    if (e.key === "ArrowRight" && i < length - 1) {
      e.preventDefault();
      focusIndex(i + 1);
    }
  };

  const handlePaste = (i, e) => {
    const text = e.clipboardData?.getData("text") || "";
    const digits = text.replace(/\D/g, "");
    if (!digits) return;
    e.preventDefault();
    const next = [...vals];
    for (let k = 0; k < digits.length && i + k < length; k += 1) {
      next[i + k] = digits[k];
    }
    commit(next);
    const lastFilled = Math.min(i + digits.length, length) - 1;
    focusIndex(Math.min(lastFilled + 1, length - 1));
  };

  return (
    <div className={`otp-container${invalid ? " has-error" : ""}`}>
      {vals.map((v, i) => (
        <input
          key={i}
          ref={(el) => setRef(el, i)}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={v}
          maxLength={1}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          onFocus={(e) => e.target.select?.()}
          aria-label={`Verification code digit ${i + 1}`}
          aria-invalid={invalid || undefined}
          className="otp-box"
        />
      ))}
    </div>
  );
};

// --- Validation ---
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/; // Indian mobile: 10 digits starting 6–9
const NAME_REGEX = /^[\p{L}][\p{L}\s.'-]*$/u; // letters (any script), spaces, . ' -
const PASSWORD_REGEX = /^(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
const OTP_LENGTH = 6;

const normalizeEmail = (value) => value.trim().toLowerCase();

const requiredText = (label, value, min = 2) => {
  const text = value.trim();
  if (!text) return `${label} is required`;
  if (text.length < min) return `${label} must be at least ${min} characters`;
  return "";
};

/* One validator per field: (value, form) → error message, or "" when valid. */
const VALIDATORS = {
  name: (value) => {
    const error = requiredText("Full name", value);
    if (error) return error;
    return NAME_REGEX.test(value.trim())
      ? ""
      : "Full name can only contain letters, spaces, . ' and -";
  },
  designation: (value) => requiredText("Designation", value),
  email: (value) => {
    if (!value.trim()) return "Work email is required";
    return EMAIL_REGEX.test(value.trim()) ? "" : "Enter a valid email address";
  },
  companyName: (value) => requiredText("Company name", value),
  industry: (value) => (value ? "" : "Please select an industry"),
  mobile: (value) => {
    if (!value) return "Mobile number is required";
    return MOBILE_REGEX.test(value)
      ? ""
      : "Enter a valid 10-digit mobile number";
  },
  password: (value) => {
    if (!value) return "Password is required";
    return PASSWORD_REGEX.test(value)
      ? ""
      : "Must be at least 8 characters with 1 number and 1 special character";
  },
  confirmPassword: (value, form) => {
    if (!value) return "Please confirm your password";
    return value === form.password ? "" : "Passwords do not match";
  },
  agreeTerms: (value) =>
    value ? "" : "You must agree to the Terms & Conditions",
};

/* On-screen order — the first invalid field in this list receives focus. */
const FIELD_ORDER = [
  "name",
  "designation",
  "email",
  "emailOTP",
  "companyName",
  "industry",
  "mobile",
  "password",
  "confirmPassword",
  "agreeTerms",
];

/*
 * Errors are derived from state rather than stored, so they can never drift
 * out of sync with the values. A field shows its error once it has been
 * blurred (touched) or a submit was attempted, and then updates live.
 *
 * Email verification is reported where the user can act on it: until a code
 * has been sent to the current address the error sits on the (always
 * visible) email field; once the code field is showing, it sits on that.
 */
const getErrors = (form, { touched, submitted, otpSentTo }) => {
  const errors = {};

  Object.entries(VALIDATORS).forEach(([field, validate]) => {
    if (!submitted && !touched[field]) return;
    const message = validate(form[field], form);
    if (message) errors[field] = message;
  });

  if (submitted) {
    const otpSentForEmail =
      Boolean(otpSentTo) && otpSentTo === normalizeEmail(form.email);
    if (!errors.email && !otpSentForEmail) {
      errors.email = "Please verify your email to continue";
    } else if (otpSentForEmail && form.emailOTP.length !== OTP_LENGTH) {
      errors.emailOTP = `Enter the ${OTP_LENGTH}-digit verification code`;
    }
  }

  return errors;
};

// --- Main Signup Component ---
const Signup = () => {
  const hasFetched = useRef(false);
  const nameRef = useRef(null);
  const navigate = useNavigate();
  const goBack = useGoBack();
  const location = useLocation();

  // Regex Patterns
  // State Management
  const [form, setForm] = useState({
    name: "",
    email: "",
    emailOTP: "",
    mobile: "",
    // mobileOTP: "",
    companyName: "",
    designation: "",
    industry: "",
    password: "",
    confirmPassword: "",
    agreeTerms: false,
  });

  const [inviteToken, setInviteToken] = useState(null);
  const [isInvited, setIsInvited] = useState(false);
  const [invitedCompany, setInvitedCompany] = useState("");
  const [invitedIndustry, setInvitedIndustry] = useState("");
  const [invitedEmail, setInvitedEmail] = useState("");

  const formRef = useRef(null);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Email the current verification code was sent to (normalized). Editing
  // the email afterwards invalidates the code and hides the OTP field.
  const [otpSentTo, setOtpSentTo] = useState("");
  const [otpKey, setOtpKey] = useState(0); // remounts the OTP boxes on resend
  const [sendingOtp, setSendingOtp] = useState(false);
  const emailOtpSent =
    Boolean(otpSentTo) && otpSentTo === normalizeEmail(form.email);
  // const [mobileOtpSent, setMobileOtpSent] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [industryOptions, setIndustryOptions] = useState([]);

  const errors = getErrors(form, {
    touched,
    submitted: submitAttempted,
    otpSentTo,
  });

  // Auto-focus name field on load
  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  // Auto-hide notification
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Fetch Industry Data
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    const fetchIndustryData = async () => {
      try {
        const response = await apiGet("user", "/master/industry");
        if (response?.data) {
          setIndustryOptions(response.data);
        }
      } catch (error) {
        console.error("Error fetching industry data:", error);
      }
    };
    fetchIndustryData();
  }, []);

  // Check invited user status
  useEffect(() => {
    const checkInviteStatus = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get("invite");
      if (!token) return;
      setInviteToken(token);

      try {
        const response = await apiGet(
          "user",
          `/check-user-status?invite=${token}`,
        );

        if (response?.data?.invited) {
          const invitedUser = response.data.user || {};
          const org = response.data.organization || {};

          setIsInvited(true);
          setInvitedEmail(invitedUser.email || "");
          setInvitedCompany(org.companyName || "");
          setInvitedIndustry(org.industry || "");

          setForm((prev) => ({
            ...prev,
            name: invitedUser.name ?? prev.name,
            email: invitedUser.email ?? prev.email,
            mobile: org.mobile ?? prev.mobile,
            companyName: org.companyName ?? prev.companyName,
            designation: org.designation ?? prev.designation,
            industry: org.industry ?? prev.industry,
          }));
        } else {
          setNotification({
            type: "error",
            message: "Invalid or expired invitation link",
          });
        }
      } catch (error) {
        setNotification({
          type: "error",
          message:
            error?.response?.data?.message ||
            "Invalid or expired invitation link",
        });
      }
    };

    checkInviteStatus();
  }, [location.search]);

  // Handle Input Changes
  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // A code is tied to the address it was sent to — drop any typed digits
      // when the address actually changes.
      if (
        field === "email" &&
        normalizeEmail(value) !== normalizeEmail(prev.email)
      ) {
        next.emailOTP = "";
      }
      return next;
    });
  };

  const touch = (field) =>
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));

  // Move the user to the first field that needs attention.
  const focusField = (field) => {
    const root = formRef.current;
    if (!root) return;
    const selector =
      field === "emailOTP"
        ? ".otp-box"
        : field === "agreeTerms"
          ? "#terms"
          : `[name="${field}"]`;
    const el = root.querySelector(selector);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
  };

  // API Call: Send Email OTP
  const sendEmailOtp = async () => {
    if (sendingOtp) return;

    // The registration call needs a valid name and email.
    setTouched((prev) => ({ ...prev, name: true, email: true }));
    const firstInvalid = ["name", "email"].find(
      (field) => VALIDATORS[field](form[field], form),
    );
    if (firstInvalid) {
      focusField(firstInvalid);
      return;
    }

    setSendingOtp(true);
    try {
      const email = form.email.trim();
      const response = await apiPost("user", "/email-register", {
        name: form.name.trim(),
        email,
        inviteToken: inviteToken,
      });
      setOtpSentTo(normalizeEmail(email));
      setForm((prev) => ({ ...prev, emailOTP: "" }));
      setOtpKey((key) => key + 1);
      setNotification({
        type: "success",
        message: response?.message || "OTP sent successfully",
      });
    } catch (error) {
      setNotification({ type: "error", message: getErrorMessage(error) });
    } finally {
      setSendingOtp(false);
    }
  };

  // API Call: Send Mobile OTP
  // const sendMobileOtp = async () => {
  //   if (
  //     !validateField("name", form.name) ||
  //     !validateField("mobile", form.mobile)
  //   )
  //     return;

  //   try {
  //     const response = await apiPost("user", "/mobile-register", {
  //       name: form.name,
  //       mobile: form.mobile,
  //     });
  //     setMobileOtpSent(true);
  //     setNotification({
  //       type: "success",
  //       message: response?.message || "OTP sent successfully",
  //     });
  //   } catch (error) {
  //     setNotification({
  //       type: "error",
  //       message:
  //         error?.response?.data?.message ||
  //         error?.message ||
  //         "Failed to send OTP",
  //     });
  //   }
  // };

  // API Call: Handle Signup
  const handleSignup = async (e) => {
    e?.preventDefault();
    if (isSubmitting) return;

    setSubmitAttempted(true);
    // Validate synchronously — the state update above lands after this runs.
    const submitErrors = getErrors(form, {
      touched,
      submitted: true,
      otpSentTo,
    });
    const firstInvalid = FIELD_ORDER.find((field) => submitErrors[field]);
    if (firstInvalid) {
      focusField(firstInvalid);
      return;
    }

    setIsSubmitting(true);
    const payload = {
      email: form.email.trim(),
      mobile: form.mobile,
      emailOtp: form.emailOTP,
      // mobileOtp: form.mobileOTP,
      name: form.name.trim(),
      password: form.password,
      companyName: form.companyName.trim(),
      designation: form.designation.trim(),
      industry: form.industry,
    };

    try {
      const response = await apiPost("user", "/signup", payload);
      const { token, id, orgId, roleId, userType, companyName } = response.data;
      const user = { id, orgId, roleId, userType, companyName };

      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      localStorage.setItem("authToken", token);
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("companyName", user.companyName);

      connectSocket(user);

      const permRes = await apiGet("user", "/modules/sidebar");
      if (permRes?.status === "success") {
        localStorage.setItem("permissions", JSON.stringify(permRes.data));
        localStorage.setItem("permissionsLoaded", "true");
      }

      setNotification({ type: "success", message: "Signup successful" });

      if (userType === "Internal") {
        navigate("/admin/dashboard");
      } else {
        navigate("/billing/pricing");
      }
    } catch (error) {
      console.error("Signup Error:", error);
      let message = "Signup failed";
      if (error?.message) {
        try {
          const parsed = JSON.parse(error.message);
          message = parsed.message || message;
        } catch {
          message = error.message;
        }
      }
      setNotification({ type: "error", message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="page-auth-page">
        <Link to="/" className="auth-brand" aria-label="Go to Doql homepage">
          <BrandLogo variant="header" />
        </Link>

        {/* Creative Visual Sidebar */}
        <aside className="branding-panel">
          <CoreNetworkVisual />
        </aside>

        {/* Signup Workspace */}
        <main className="form-panel">
          <AnimatePresence>
            {notification && (
              <motion.div
                className={`notification-toast type-${notification.type}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                <Icons.Shield /> {notification.message}
              </motion.div>
            )}
          </AnimatePresence>

          <AuthCard>
            <button className="back-action" onClick={goBack}>
              <Icons.ChevronLeft /> Back
            </button>

            <header className="header-block">
              <h1>Enterprise Signup</h1>
              <p>
                Scale your regulated products with Doql secure infrastructure.
              </p>
            </header>

            <form
              ref={formRef}
              className="signup-form"
              onSubmit={handleSignup}
              noValidate
            >
              {/* Profile Section */}
              <section className="section-container">
                <span className="section-header">Personal Information</span>
                <div className="input-row">
                  <div className="field-group">
                    <label>Full Name</label>
                    <div className="control-wrapper">
                      <input
                        ref={nameRef}
                        name="name"
                        autoComplete="name"
                        placeholder="Ananya Iyer"
                        value={form.name}
                        aria-invalid={Boolean(errors.name)}
                        onChange={(e) => handleChange("name", e.target.value)}
                        onBlur={() => touch("name")}
                      />
                    </div>
                    {errors.name && (
                      <span className="error-hint">{errors.name}</span>
                    )}
                  </div>

                  <div className="field-group">
                    <label>Designation</label>
                    <div className="control-wrapper">
                      <input
                        name="designation"
                        autoComplete="organization-title"
                        placeholder="e.g. Compliance Officer"
                        value={form.designation}
                        aria-invalid={Boolean(errors.designation)}
                        onChange={(e) =>
                          handleChange("designation", e.target.value)
                        }
                        onBlur={() => touch("designation")}
                      />
                    </div>
                    {errors.designation && (
                      <span className="error-hint">{errors.designation}</span>
                    )}
                  </div>
                </div>

                <div className="field-group">
                  <label>Work Email</label>
                  <div className="control-wrapper">
                    <input
                      type="email"
                      name="email"
                      autoComplete="email"
                      placeholder="name@company.com"
                      value={form.email}
                      disabled={isInvited && !!invitedEmail}
                      aria-invalid={Boolean(errors.email)}
                      onChange={(e) => handleChange("email", e.target.value)}
                      onBlur={() => touch("email")}
                    />
                    <button
                      type="button"
                      className="otp-trigger"
                      onClick={sendEmailOtp}
                      disabled={sendingOtp}
                    >
                      {sendingOtp
                        ? "Sending..."
                        : emailOtpSent
                          ? "Resend"
                          : "Verify Email"}
                    </button>
                  </div>
                  {errors.email && (
                    <span className="error-hint">{errors.email}</span>
                  )}
                </div>

                {emailOtpSent && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="field-group"
                  >
                    <label>Email Verification Code</label>
                    <div className="control-wrapper">
                      <OTPField
                        key={otpKey}
                        invalid={Boolean(errors.emailOTP)}
                        onChange={(val) => handleChange("emailOTP", val)}
                      />
                    </div>
                    {errors.emailOTP && (
                      <span className="error-hint">{errors.emailOTP}</span>
                    )}
                  </motion.div>
                )}
              </section>

              {/* Organization Section */}
              <section className="section-container">
                <span className="section-header">Organization</span>
                <div className="input-row">
                  <div className="field-group">
                    <label>Company Name</label>
                    <div className="control-wrapper">
                      <input
                        name="companyName"
                        autoComplete="organization"
                        placeholder="Acme FinCorp"
                        value={form.companyName}
                        disabled={isInvited && !!invitedCompany}
                        aria-invalid={Boolean(errors.companyName)}
                        onChange={(e) =>
                          handleChange("companyName", e.target.value)
                        }
                        onBlur={() => touch("companyName")}
                      />
                    </div>
                    {errors.companyName && (
                      <span className="error-hint">{errors.companyName}</span>
                    )}
                  </div>
                  <div className="field-group">
                    <label>Industry</label>
                    <div className="control-wrapper">
                      <select
                        name="industry"
                        value={form.industry}
                        disabled={isInvited && !!invitedIndustry}
                        aria-invalid={Boolean(errors.industry)}
                        onChange={(e) => {
                          handleChange("industry", e.target.value);
                          touch("industry");
                        }}
                        onBlur={() => touch("industry")}
                      >
                        <option value="">Select Industry</option>
                        {industryOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.industry && (
                      <span className="error-hint">{errors.industry}</span>
                    )}
                  </div>
                </div>

                <div className="field-group">
                  <label>Mobile Number</label>
                  <div className="control-wrapper">
                    <input
                      type="tel"
                      name="mobile"
                      autoComplete="tel-national"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="9988776655"
                      value={form.mobile}
                      aria-invalid={Boolean(errors.mobile)}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);
                        handleChange("mobile", value);
                      }}
                      onBlur={() => touch("mobile")}
                    />
                    {/* <button className="otp-trigger" onClick={sendMobileOtp}>
                      {mobileOtpSent ? "Resend" : "Verify Mobile"}
                    </button> */}
                  </div>
                  {errors.mobile && (
                    <span className="error-hint">{errors.mobile}</span>
                  )}
                </div>

                {/* {mobileOtpSent && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="field-group"
                  >
                    <label>Mobile Verification Code</label>
                    <div className="control-wrapper">
                      <OTPField
                        onChange={(val) => handleChange("mobileOTP", val)}
                      />
                    </div>
                    {errors.mobileOTP && (
                      <span className="error-hint">{errors.mobileOTP}</span>
                    )}
                  </motion.div>
                )} */}
              </section>

              {/* Security Section */}
              <section className="section-container">
                <span className="section-header">Security</span>
                <div className="input-row">
                  <div className="field-group">
                    <label>Create Password</label>
                    <div className="control-wrapper">
                      <input
                        type={showPass ? "text" : "password"}
                        name="password"
                        autoComplete="new-password"
                        placeholder="••••••••"
                        value={form.password}
                        aria-invalid={Boolean(errors.password)}
                        onChange={(e) =>
                          handleChange("password", e.target.value)
                        }
                        onBlur={() => touch("password")}
                      />
                      <button
                        type="button"
                        className="visibility-btn"
                        onClick={() => setShowPass(!showPass)}
                        aria-label={showPass ? "Hide password" : "Show password"}
                      >
                        {showPass ? <Icons.EyeOff /> : <Icons.Eye />}
                      </button>
                    </div>
                    {errors.password && (
                      <span className="error-hint">{errors.password}</span>
                    )}
                  </div>
                  <div className="field-group">
                    <label>Confirm Password</label>
                    <div className="control-wrapper">
                      <input
                        type={showConfirmPass ? "text" : "password"}
                        name="confirmPassword"
                        autoComplete="new-password"
                        placeholder="••••••••"
                        value={form.confirmPassword}
                        aria-invalid={Boolean(errors.confirmPassword)}
                        onChange={(e) =>
                          handleChange("confirmPassword", e.target.value)
                        }
                        onBlur={() => touch("confirmPassword")}
                      />
                      <button
                        type="button"
                        className="visibility-btn"
                        onClick={() => setShowConfirmPass(!showConfirmPass)}
                        aria-label={
                          showConfirmPass ? "Hide password" : "Show password"
                        }
                      >
                        {showConfirmPass ? <Icons.EyeOff /> : <Icons.Eye />}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <span className="error-hint">
                        {errors.confirmPassword}
                      </span>
                    )}
                  </div>
                </div>
              </section>

              <div className="terms-agreement">
                <input
                  type="checkbox"
                  id="terms"
                  name="agreeTerms"
                  checked={form.agreeTerms}
                  aria-invalid={Boolean(errors.agreeTerms)}
                  onChange={(e) => {
                    handleChange("agreeTerms", e.target.checked);
                    touch("agreeTerms");
                  }}
                />
                <label htmlFor="terms">
                  I agree to the <strong>Terms of Service</strong> and
                  understand that data residency is strictly maintained within
                  India as per DPDPA guidelines.
                </label>
              </div>

              <div className="submit-action">
                {errors.agreeTerms && (
                  <span className="error-hint">{errors.agreeTerms}</span>
                )}
                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting
                    ? "Creating Account..."
                    : "Create Enterprise Account"}
                </button>
              </div>
            </form>

            <div className="login-link">
              Already have an account? <Link to="/login">Sign in</Link>
            </div>
          </AuthCard>
        </main>
      </div>
    </>
  );
};

export default Signup;
